import * as Schema from "effect/Schema";
import * as RpcMiddleware from "effect/unstable/rpc/RpcMiddleware";

import { IsoDateTime, NonNegativeInt, PositiveInt, TrimmedNonEmptyString } from "./baseSchemas";

export const RELAY_PROTOCOL_FAMILY = "forge-relay" as const;
export const RELAY_PROTOCOL_MIN_VERSION = 1 as const;
export const RELAY_PROTOCOL_VERSION = 4 as const;
export const RELAY_REMOTE_CONTROL_MIN_VERSION = 4 as const;
export const DESKTOP_RELAY_CONTROL_PLANE_PATH = "/api/desktop/relay-control-plane" as const;
export const DESKTOP_RELAY_BRIDGE_TOKEN_HEADER = "x-forge-relay-bridge-token" as const;

export const RELAY_REQUEST_HEADERS = {
  requestId: "x-forge-relay-request-id",
  sequence: "x-forge-relay-sequence",
  issuedAt: "x-forge-relay-issued-at",
  expiresAt: "x-forge-relay-expires-at",
  payloadHash: "x-forge-relay-payload-hash",
} as const;

export const DesktopRelayControlPlaneRequest = Schema.Struct({
  version: Schema.Literal(1),
  runtimeId: TrimmedNonEmptyString,
  path: TrimmedNonEmptyString,
  body: Schema.Unknown,
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type DesktopRelayControlPlaneRequest = typeof DesktopRelayControlPlaneRequest.Type;

export const RelayCapability = Schema.Literals([
  "rpc.read",
  "rpc.mutate",
  "cursor.resume",
  "terminal.stream",
  "browser.stream",
  "bulk.transfer",
  "envelope.validation",
  "payload.e2e",
]);
export type RelayCapability = typeof RelayCapability.Type;

export const RELAY_SCOPES = [
  "project.read",
  "project.write",
  "workspace.read",
  "workspace.write",
  "thread.read",
  "thread.create",
  "thread.mutate",
  "turn.dispatch",
  "turn.stop",
  "approval.respond",
  "terminal.read",
  "terminal.write",
  "browser.read",
  "browser.control",
  "git.read",
  "git.write",
  "diff.read",
  "shell.open",
  "provider.read",
  "provider.manage",
  "settings.read",
  "settings.write",
  "runtime.read",
  "runtime.manage",
  "runtime.admin",
] as const;

export const RelayScope = Schema.Literals(RELAY_SCOPES);
export type RelayScope = typeof RelayScope.Type;

export const RELAY_FULL_CONTROL_SCOPES: ReadonlyArray<Exclude<RelayScope, "runtime.admin">> =
  RELAY_SCOPES.filter(
    (scope): scope is Exclude<RelayScope, "runtime.admin"> => scope !== "runtime.admin",
  );

export const RelayDeliveryClass = Schema.Literals([
  "read",
  "idempotent-command",
  "create-command",
  "subscription",
  "terminal-input",
  "terminal-output",
  "browser-control",
  "browser-frame",
  "bulk-transfer",
]);
export type RelayDeliveryClass = typeof RelayDeliveryClass.Type;

export const RelayJournalPersistence = Schema.Literals(["none", "metadata-only", "frame"]);
export type RelayJournalPersistence = typeof RelayJournalPersistence.Type;

const RelayConnectionHelloBase = Schema.Struct({
  protocolFamily: Schema.Literal(RELAY_PROTOCOL_FAMILY),
  minVersion: PositiveInt,
  maxVersion: PositiveInt,
  buildVersion: TrimmedNonEmptyString,
  supportedCapabilities: Schema.Array(RelayCapability),
  requiredCapabilities: Schema.Array(RelayCapability),
  connectionEpoch: NonNegativeInt,
  resumeCursor: Schema.optionalKey(TrimmedNonEmptyString),
});

export const RelayConnectionHello = RelayConnectionHelloBase.check(
  Schema.makeFilter((hello) => hello.minVersion <= hello.maxVersion, {
    expected: "a relay protocol version range where minVersion is not greater than maxVersion",
  }),
);
export type RelayConnectionHello = typeof RelayConnectionHello.Type;

export const RelayMessageType = Schema.Literals([
  "request",
  "response",
  "event",
  "acknowledgement",
  "error",
]);
export type RelayMessageType = typeof RelayMessageType.Type;

const RelayRpcEnvelopeFields = {
  messageType: RelayMessageType,
  requestId: TrimmedNonEmptyString,
  correlationId: TrimmedNonEmptyString,
  runtimeId: TrimmedNonEmptyString,
  connectionEpoch: NonNegativeInt,
  actor: Schema.Struct({
    userId: PositiveInt,
    deviceId: PositiveInt,
    sessionId: TrimmedNonEmptyString,
  }),
  requiredScope: Schema.NullOr(RelayScope),
  deliveryClass: RelayDeliveryClass,
  issuedAt: IsoDateTime,
  expiresAt: IsoDateTime,
  payloadHash: TrimmedNonEmptyString,
  method: TrimmedNonEmptyString,
  sequence: Schema.optionalKey(NonNegativeInt),
  resumeCursor: Schema.optionalKey(TrimmedNonEmptyString),
} as const;

export const RelayRpcEnvelopeV2 = Schema.Struct({
  protocolVersion: Schema.Literal(2),
  ...RelayRpcEnvelopeFields,
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type RelayRpcEnvelopeV2 = typeof RelayRpcEnvelopeV2.Type;

export const RelayRpcEnvelopeV3 = Schema.Struct({
  protocolVersion: Schema.Literal(3),
  ...RelayRpcEnvelopeFields,
  actionGrant: Schema.optionalKey(TrimmedNonEmptyString),
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type RelayRpcEnvelopeV3 = typeof RelayRpcEnvelopeV3.Type;

export const RelayRpcEnvelopeV4 = Schema.Struct({
  protocolVersion: Schema.Literal(4),
  ...RelayRpcEnvelopeFields,
  actionGrant: Schema.optionalKey(TrimmedNonEmptyString),
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type RelayRpcEnvelopeV4 = typeof RelayRpcEnvelopeV4.Type;

export const RelayRpcEnvelope = Schema.Union([
  RelayRpcEnvelopeV2,
  RelayRpcEnvelopeV3,
  RelayRpcEnvelopeV4,
]);
export type RelayRpcEnvelope = typeof RelayRpcEnvelope.Type;

export const RelayActionGrantCommandType = Schema.Literals([
  "thread.approval.respond",
  "thread.user-input.respond",
  "thread.plan-approval.respond",
]);
export type RelayActionGrantCommandType = typeof RelayActionGrantCommandType.Type;

const RelayActionGrantRequestFields = {
  method: Schema.Literal("orchestration.dispatchCommand"),
  commandType: RelayActionGrantCommandType,
  commandId: TrimmedNonEmptyString,
  targetId: TrimmedNonEmptyString,
  durableRequestId: TrimmedNonEmptyString,
  payloadHash: TrimmedNonEmptyString,
  requiredScope: Schema.Literal("approval.respond"),
} as const;

export const RelayActionGrantRequestV3 = Schema.Struct({
  protocolVersion: Schema.Literal(3),
  ...RelayActionGrantRequestFields,
}).annotate({ parseOptions: { onExcessProperty: "error" } });

export const RelayActionGrantRequestV4 = Schema.Struct({
  protocolVersion: Schema.Literal(4),
  channelId: TrimmedNonEmptyString,
  channelTicketDigest: TrimmedNonEmptyString,
  runtimeEndpointKeyId: TrimmedNonEmptyString,
  controlEndpointKeyId: TrimmedNonEmptyString,
  ...RelayActionGrantRequestFields,
}).annotate({ parseOptions: { onExcessProperty: "error" } });

export const RelayActionGrantRequest = Schema.Union([
  RelayActionGrantRequestV3,
  RelayActionGrantRequestV4,
]);
export type RelayActionGrantRequest = typeof RelayActionGrantRequest.Type;

export const RelayVerificationKey = Schema.Struct({
  kty: Schema.Literal("OKP"),
  crv: Schema.Literal("Ed25519"),
  x: TrimmedNonEmptyString,
  kid: TrimmedNonEmptyString,
  alg: Schema.Literal("EdDSA"),
  use: Schema.Literal("sig"),
  notBefore: Schema.optionalKey(IsoDateTime),
  expiresAt: Schema.optionalKey(IsoDateTime),
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type RelayVerificationKey = typeof RelayVerificationKey.Type;

export const RelayVerificationKeySet = Schema.Struct({
  keys: Schema.Array(RelayVerificationKey),
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type RelayVerificationKeySet = typeof RelayVerificationKeySet.Type;

export const RelayMethodPolicy = Schema.Struct({
  method: TrimmedNonEmptyString,
  requiredScope: Schema.NullOr(RelayScope),
  deliveryClass: RelayDeliveryClass,
  journalPersistence: RelayJournalPersistence,
});
export type RelayMethodPolicy = typeof RelayMethodPolicy.Type;

export const RelayProtocolErrorCode = Schema.Literals([
  "protocol_version_mismatch",
  "required_capability_missing",
  "method_not_remotable",
  "scope_denied",
  "stale_connection_epoch",
  "request_expired",
  "request_in_progress",
  "request_payload_mismatch",
  "protocol_update_required",
  "action_grant_required",
  "action_grant_invalid",
  "resume_cursor_expired",
  "resume_cursor_ahead",
  "end_to_end_encryption_required",
  "hpke_suite_unsupported",
  "endpoint_key_invalid",
  "channel_ticket_binding_invalid",
  "channel_confirmation_failed",
  "channel_sequence_invalid",
  "channel_authentication_failed",
  "channel_limit_reached",
  "channel_truncated",
]);
export type RelayProtocolErrorCode = typeof RelayProtocolErrorCode.Type;

export class RelayAuthorizationError extends Schema.TaggedErrorClass<RelayAuthorizationError>()(
  "RelayAuthorizationError",
  {
    code: RelayProtocolErrorCode,
    method: TrimmedNonEmptyString,
    detail: TrimmedNonEmptyString,
  },
) {
  // fallow-ignore-next-line unused-class-member
  override get message(): string {
    return this.detail;
  }
}

export class RelayAuthorizationMiddleware extends RpcMiddleware.Service<RelayAuthorizationMiddleware>()(
  "@cmmd-forge/contracts/RelayAuthorizationMiddleware",
  { error: RelayAuthorizationError },
) {}

export class RelayDeliveryError extends Schema.TaggedErrorClass<RelayDeliveryError>()(
  "RelayDeliveryError",
  {
    code: TrimmedNonEmptyString,
    requestId: Schema.NullOr(TrimmedNonEmptyString),
    detail: TrimmedNonEmptyString,
    retryable: Schema.Boolean,
  },
) {}

export class RelayDeliveryMiddleware extends RpcMiddleware.Service<RelayDeliveryMiddleware>()(
  "@cmmd-forge/contracts/RelayDeliveryMiddleware",
  { error: RelayDeliveryError, requiredForClient: true },
) {}

export const RelayLocalBridgeContext = Schema.Struct({
  connectionId: TrimmedNonEmptyString,
  runtimeId: TrimmedNonEmptyString,
  connectionEpoch: PositiveInt,
  protocolVersion: PositiveInt,
  actor: Schema.Struct({
    userId: PositiveInt,
    deviceId: PositiveInt,
    sessionId: TrimmedNonEmptyString,
  }),
  organizationId: Schema.optionalKey(PositiveInt),
  projectId: Schema.optionalKey(Schema.NullOr(PositiveInt)),
  accessGrantId: Schema.optionalKey(Schema.NullOr(PositiveInt)),
  scopes: Schema.Array(RelayScope),
});
export type RelayLocalBridgeContext = typeof RelayLocalBridgeContext.Type;

export const DesktopRelayHostStatus = Schema.Literals([
  "stopped",
  "connecting",
  "authenticating",
  "connected",
  "reconnecting",
  "blocked",
]);
export type DesktopRelayHostStatus = typeof DesktopRelayHostStatus.Type;

export const DesktopRelayIdentityStatus = Schema.Literals([
  "trusted",
  "rotating",
  "revoked",
  "secure-storage-unavailable",
]);
export type DesktopRelayIdentityStatus = typeof DesktopRelayIdentityStatus.Type;

export const DesktopRelayHostState = Schema.Struct({
  status: DesktopRelayHostStatus,
  attempt: NonNegativeInt,
  identityStatus: Schema.optionalKey(DesktopRelayIdentityStatus),
  relayNodeId: Schema.optionalKey(TrimmedNonEmptyString),
  connectionEpoch: Schema.optionalKey(PositiveInt),
  controlCount: Schema.optionalKey(NonNegativeInt),
  errorCode: Schema.optionalKey(TrimmedNonEmptyString),
  error: Schema.optionalKey(TrimmedNonEmptyString),
  retryAt: Schema.optionalKey(TrimmedNonEmptyString),
});
export type DesktopRelayHostState = typeof DesktopRelayHostState.Type;

export const RuntimeReplicationStatus = Schema.Struct({
  state: Schema.Literals(["synced", "pending", "blocked"]),
  pending: NonNegativeInt,
  blocked: NonNegativeInt,
  lastError: Schema.NullOr(TrimmedNonEmptyString),
});
export type RuntimeReplicationStatus = typeof RuntimeReplicationStatus.Type;

export const RelayTransportAuthMessage = Schema.Struct({
  type: Schema.Literal("relay.auth"),
  ticket: TrimmedNonEmptyString,
  protocolMinVersion: PositiveInt,
  protocolMaxVersion: PositiveInt,
  capabilities: Schema.Array(RelayCapability),
  controlToRuntimeEncapsulatedKey: Schema.optionalKey(TrimmedNonEmptyString),
});
export type RelayTransportAuthMessage = typeof RelayTransportAuthMessage.Type;

export const RelayTransportAuthenticatedMessage = Schema.Struct({
  type: Schema.Literal("relay.authenticated"),
  role: Schema.Literals(["host", "control"]),
  runtimeId: TrimmedNonEmptyString,
  connectionEpoch: NonNegativeInt,
  protocolVersion: PositiveInt,
  scopes: Schema.optionalKey(Schema.Array(RelayScope)),
  actor: Schema.optionalKey(
    Schema.Struct({
      userId: PositiveInt,
      deviceId: PositiveInt,
      sessionId: TrimmedNonEmptyString,
    }),
  ),
});
export type RelayTransportAuthenticatedMessage = typeof RelayTransportAuthenticatedMessage.Type;

export const RelayTransportControlAttachedMessage = Schema.Struct({
  type: Schema.Literal("relay.control-attached"),
  connectionId: TrimmedNonEmptyString,
  ticket: Schema.optionalKey(TrimmedNonEmptyString),
  actor: Schema.Struct({
    userId: PositiveInt,
    deviceId: PositiveInt,
    sessionId: TrimmedNonEmptyString,
  }),
  organizationId: Schema.optionalKey(PositiveInt),
  projectId: Schema.optionalKey(Schema.NullOr(PositiveInt)),
  accessGrantId: Schema.optionalKey(Schema.NullOr(PositiveInt)),
  scopes: Schema.Array(RelayScope),
  connectionEpoch: NonNegativeInt,
  protocolVersion: PositiveInt,
  controlToRuntimeEncapsulatedKey: Schema.optionalKey(TrimmedNonEmptyString),
});
export type RelayTransportControlAttachedMessage = typeof RelayTransportControlAttachedMessage.Type;

export const RelayTransportControlDetachedMessage = Schema.Struct({
  type: Schema.Literal("relay.control-detached"),
  connectionId: TrimmedNonEmptyString,
});
export type RelayTransportControlDetachedMessage = typeof RelayTransportControlDetachedMessage.Type;

export const RelayTransportDetachControlMessage = Schema.Struct({
  type: Schema.Literal("relay.detach-control"),
  connectionId: TrimmedNonEmptyString,
});
export type RelayTransportDetachControlMessage = typeof RelayTransportDetachControlMessage.Type;

export const RelayTransportFrameMessage = Schema.Struct({
  type: Schema.Literal("relay.frame"),
  connectionId: Schema.optionalKey(TrimmedNonEmptyString),
  payload: Schema.String,
  envelope: Schema.optionalKey(RelayRpcEnvelope),
});
export type RelayTransportFrameMessage = typeof RelayTransportFrameMessage.Type;

export const RelayPayloadDirection = Schema.Literals(["control-to-runtime", "runtime-to-control"]);
export type RelayPayloadDirection = typeof RelayPayloadDirection.Type;

export const RelayPayloadFrameClass = Schema.Literals(["handshake", "application", "close"]);
export type RelayPayloadFrameClass = typeof RelayPayloadFrameClass.Type;

export const RelayEndpointConfirmation = Schema.Struct({
  type: Schema.Literal("relay.endpoint-confirmation"),
  protocolVersion: Schema.Literal(4),
  phase: Schema.Literals(["runtime-hello", "control-confirm", "runtime-confirm"]),
  channelId: TrimmedNonEmptyString,
  runtimeId: TrimmedNonEmptyString,
  connectionEpoch: NonNegativeInt,
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type RelayEndpointConfirmation = typeof RelayEndpointConfirmation.Type;

export const RelayEncryptedApplicationPayload = Schema.Struct({
  payload: Schema.String,
  envelope: RelayRpcEnvelopeV4,
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type RelayEncryptedApplicationPayload = typeof RelayEncryptedApplicationPayload.Type;

export const RelayTransportEncryptedFrameMessage = Schema.Struct({
  type: Schema.Literal("relay.encrypted-frame"),
  protocolVersion: Schema.Literal(4),
  connectionId: Schema.optionalKey(TrimmedNonEmptyString),
  channelId: TrimmedNonEmptyString,
  direction: RelayPayloadDirection,
  frameClass: RelayPayloadFrameClass,
  sequence: NonNegativeInt,
  ciphertext: TrimmedNonEmptyString,
  encapsulatedKey: Schema.optionalKey(TrimmedNonEmptyString),
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type RelayTransportEncryptedFrameMessage = typeof RelayTransportEncryptedFrameMessage.Type;

export const RelayTransportClientMessage = Schema.Union([
  RelayTransportAuthMessage,
  RelayTransportDetachControlMessage,
  RelayTransportFrameMessage,
  RelayTransportEncryptedFrameMessage,
]);
export type RelayTransportClientMessage = typeof RelayTransportClientMessage.Type;

export const RelayTransportErrorMessage = Schema.Struct({
  type: Schema.Literal("relay.error"),
  code: TrimmedNonEmptyString,
  message: TrimmedNonEmptyString,
});
export type RelayTransportErrorMessage = typeof RelayTransportErrorMessage.Type;

export const RelayTransportServerMessage = Schema.Union([
  RelayTransportAuthenticatedMessage,
  RelayTransportControlAttachedMessage,
  RelayTransportControlDetachedMessage,
  RelayTransportFrameMessage,
  RelayTransportEncryptedFrameMessage,
  RelayTransportErrorMessage,
]);
export type RelayTransportServerMessage = typeof RelayTransportServerMessage.Type;
